import { useCallback, useRef, useState } from 'react';
import { listUsers } from '../api/adminUsersApi';
import { logError } from '../utils/errorHandling';
import type { components } from '../api/types.gen';

type UserResponse = components['schemas']['UserResponse'];

const DEFAULT_CHUNK_SIZE = 100;

/** id → user object cache, keyed by the user's id coerced to a string key. */
export type UserNamesMap = Record<string, UserResponse>;

/**
 * Display label for a user id resolved through {@link useUserNameResolver}.
 * `UserResponse.name` is `string | null`, so the email fallback is load
 * bearing: a `student ? student.name : ...` chain renders the literal
 * `null` for a user record with no name.
 *
 * @param names - the map returned by useUserNameResolver
 * @param id - the user id, number or string (the map is keyed by string)
 */
export function userLabel(names: UserNamesMap, id: number | string): string {
  const user = names[String(id)];
  return user?.name || user?.email || `Student #${id}`;
}

/**
 * What {@link useUserNameResolver}'s `resolveNames` resolves to.
 *
 * `failedIds` are the ids whose `listUsers` chunk THREW — a retryable
 * failure, distinct from an id the server positively did not return (that
 * user is gone, not unknown, and retrying cannot help). Without this a
 * caller can only see a gap in `names` and cannot tell the two apart.
 * Keys are stringified to match {@link UserNamesMap}'s keying.
 */
export interface ResolveNamesResult {
  failedIds: string[];
}

/**
 * Tuple returned by {@link useUserNameResolver}:
 * `[names, resolveNames, mergeNames]`. `mergeNames` lets a caller fold in
 * user objects it already has from another API call (e.g. a search result)
 * without a redundant `listUsers({ ids })` round trip.
 */
export type UseUserNameResolverResult = [
  UserNamesMap,
  (ids: Array<number | string>) => Promise<ResolveNamesResult>,
  (users: UserResponse[]) => void,
];

/**
 * Resolves user ids to {id, name, email} via adminUsersApi.listUsers({ids}),
 * chunked and cached across calls (a ref cache so repeated calls with
 * already-known ids skip the network). Used to display denormalized
 * teacher/student names for ids-only relations (ClassSectionsPanel's
 * teacherId, StudentPassLimitsPanel's studentId).
 *
 * @param logContext - passed to logError on a per-chunk failure
 * @param chunkSize - number of ids per listUsers request
 * @returns [names, resolveNames, mergeNames]; `resolveNames` resolves to
 *          `{ failedIds }` — see {@link ResolveNamesResult}
 */
export function useUserNameResolver(
  logContext: string,
  chunkSize: number = DEFAULT_CHUNK_SIZE
): UseUserNameResolverResult {
  const [names, setNames] = useState<UserNamesMap>({});
  const namesRef = useRef<UserNamesMap>({});

  const mergeNames = useCallback((users: UserResponse[]): void => {
    const entries: UserNamesMap = {};
    users.forEach((u) => {
      entries[u.id] = u;
    });
    namesRef.current = { ...namesRef.current, ...entries };
    setNames((prev) => ({ ...prev, ...entries }));
  }, []);

  const resolveNames = useCallback(
    async (ids: Array<number | string>): Promise<ResolveNamesResult> => {
      const unresolved = [...new Set(ids)].filter(
        (id) => !namesRef.current[id]
      );
      if (unresolved.length === 0) return { failedIds: [] };

      const newEntries: UserNamesMap = {};
      const failedIds: string[] = [];
      for (let i = 0; i < unresolved.length; i += chunkSize) {
        const chunk = unresolved.slice(i, i + chunkSize);
        try {
          const result = await listUsers({ ids: chunk, limit: chunk.length });
          result.data.forEach((u) => {
            newEntries[u.id] = u;
          });
        } catch (error) {
          logError(error, logContext);
          // The whole chunk is unknown, not gone: nothing came back that
          // could distinguish its members.
          chunk.forEach((id) => failedIds.push(String(id)));
        }
      }
      if (Object.keys(newEntries).length > 0)
        mergeNames(Object.values(newEntries));
      return { failedIds };
    },
    [logContext, chunkSize, mergeNames]
  );

  return [names, resolveNames, mergeNames];
}

export default useUserNameResolver;
